import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import firestoreService from '../../services/firestoreService';
import { UserStatus, UserRole } from '../../enums/auth';
import useCrypto from '../../hooks/common/useCrypto';

export const useAuth = () => {
  const { encrypto } = useCrypto();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // 인증 상태 리스너 설정
    const unsubscribe = authService.onAuthStateChanged((user) => {
      setUser(user);
      setLoading(false);
      setError(null);
    });

    return () => unsubscribe();
  }, []);

  // 로그인
  const login = useCallback(async (id, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await authService.login(id, password);
      
      if (result.success) {
        // 사용자 데이터 가져오기
        setUser(result.user);
        saveLastLoginAt(result.user.uid);
      } else {
        setError(result.error);
        return result;
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);
  
  const saveLastLoginAt = useCallback(async (uid) => {
    try {
      const result = await firestoreService.updateDocument('User', uid, {
        last_login_at: new Date()
      });
    } catch (error) {
      console.error('Failed to save last login time:', error);
    }
  }, []);

  // 계정 생성
  const createAccount = useCallback(async (id, password, name, phone) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await authService.createAccount(id, password, name, phone);

      if (result.success) {
        setUser(result.user);
        // 사용자 데이터 생성
        const userData = {
          email: result.user.email,
          name: name,
          role: UserRole.USER,
          status: UserStatus.REGISTERED,
          phone: encrypto(phone),
          last_login_at: new Date()
        };
        
        const createUserResult = await firestoreService.createDocument('User', userData, result.user.uid);
        console.log('User created in Firestore:', createUserResult);
        
        return result;
      } else {
        setError(result.error);
        return result;
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Google 로그인 (기존 로그인과 동일하게 last_login_at 저장)
  const googleLogin = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await authService.googleLogin();
      
      if (result.success) {
        saveLastLoginAt(result.user.uid);
      } else {
        setError(result.error);
      }
      
      return result;
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [saveLastLoginAt]);

  // 로그아웃
  const logout = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 현재 사용자 ID 저장
      const currentUserId = user?.uid;
      
      const result = await authService.logout();
      
      if (result.success) {
        setUser(null);
        
        // 명시적으로 오프라인 상태로 설정
        if (currentUserId) {
          try {
            const realtimeService = (await import('../../services/realtimeService')).default;
            realtimeService.setUserOffline(currentUserId);
          } catch (error) {
            console.warn('Failed to set user offline status:', error);
          }
        }
        
        navigate('/');
      } else {
        setError(result.error);
      }
      
      return result;
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [navigate, user]);

  // 비밀번호 재설정
  const sendPasswordReset = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await authService.sendPasswordReset(id);
      
      if (!result.success) {
        setError(result.error);
      }
      
      return result;
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // 비밀번호 재설정 확인
  const confirmPasswordReset = useCallback(async (oobCode, newPassword) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await authService.confirmPasswordReset(oobCode, newPassword);
      
      if (!result.success) {
        setError(result.error);
      }
      
      return result;
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // 사용자 삭제
  const deleteUser = useCallback(async (uid) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await authService.deleteUser(uid);
      
      if (!result.success) {
        setError(result.error);
      }
      
      return result;
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // 현재 사용자 삭제
  const deleteCurrentUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await authService.deleteCurrentUser();
      
      if (result.success) {
        setUser(null);
        navigate('/');
      } else {
        setError(result.error);
      }
      
      return result;
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // 토큰 새로고침
  const refreshToken = useCallback(async () => {
    try {
      const result = await authService.refreshUserToken();
      
      if (!result.success) {
        setError(result.error);
      }
      
      return result;
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    }
  }, []);

  // 에러 초기화
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    createAccount,
    googleLogin,
    logout,
    sendPasswordReset,
    confirmPasswordReset,
    deleteUser,
    deleteCurrentUser,
    saveLastLoginAt,
    refreshToken,
    clearError
  };
};

export default useAuth; 